import React, { Component, Suspense } from 'react'

import { PageMetadata } from './PageMetadata'

import { FullScreenLoader } from '@/components/common/FullScreenLoader'

interface RouteWrapperProps {
    component: React.ComponentType
    title?: string
    description?: string
    fallbackType?: 'auth' | 'app'
    children?: React.ReactNode
}

interface ErrorBoundaryState {
    hasError: boolean
    error?: Error
}

class ErrorBoundary extends Component<{ children: React.ReactNode }, ErrorBoundaryState> {
    constructor(props: { children: React.ReactNode }) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log error to error reporting service in production
        void error
        void errorInfo
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <h2>Something went wrong</h2>
                    <p>Please refresh the page and try again.</p>
                    <button onClick={() => this.setState({ hasError: false })}>Try again</button>
                </div>
            )
        }

        return this.props.children
    }
}

export function RouteWrapper({
    component: Component,
    title,
    description,
    fallbackType = 'auth',
    children,
}: RouteWrapperProps) {
    // Match previous fallback behavior: auth routes without noBackground, app routes with noBackground
    const fallbackProps = fallbackType === 'app' ? { noBackground: true } : {}

    return (
        <ErrorBoundary>
            <PageMetadata title={title} description={description} />
            <Suspense fallback={<FullScreenLoader {...fallbackProps} />}>
                <Component />
                {children}
            </Suspense>
        </ErrorBoundary>
    )
}
